import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'

// ──────────────────────────────────────────────
// Auth context — JWT (session token) in localStorage
// ──────────────────────────────────────────────

const TOKEN_KEY = 'ws_auth_token'

export interface AuthUser {
  _id: string
  email: string
  firstName: string
  lastName: string
  role: string
  status: string
  companyName?: string | null
}

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
  sessionReplaced: boolean
  login: (token: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  token: null,
  sessionReplaced: false,
  login: async () => {},
  logout: async () => {},
})

export function useAuth(): AuthContextValue {
  return useContext(AuthContext)
}

/** Helper: get stored token (for useApi and other fetch calls) */
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

/** Helper: get auth headers */
export function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken()
  if (!token) return {}
  return { Authorization: `Bearer ${token}` }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY))
  const [sessionReplaced, setSessionReplaced] = useState(false)

  const fetchMe = useCallback(async (t: string): Promise<AuthUser | null> => {
    try {
      const res = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${t}` },
      })
      if (!res.ok) {
        // Detect single-session replacement
        if (res.status === 401) {
          try {
            const data = await res.json()
            if (data.error === 'SESSION_REPLACED') {
              setSessionReplaced(true)
            }
          } catch { /* ignore parse errors */ }
        }
        return null
      }
      const data = await res.json()
      return data.user ?? null
    } catch {
      return null
    }
  }, [])

  // Check session on mount — handle OIDC callback first
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('oidc') === '1') {
      // Token was delivered via short-lived cookie (oidc_pending) to avoid URL exposure
      const match = document.cookie.match(/(?:^|;\s*)oidc_pending=([^;]*)/)
      const oidcToken = match ? decodeURIComponent(match[1]) : null
      if (oidcToken) {
        localStorage.setItem(TOKEN_KEY, oidcToken)
        // Expire the cookie immediately
        document.cookie = 'oidc_pending=; Path=/; Max-Age=0; SameSite=Lax'
      }
      params.delete('oidc')
      const cleanUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '')
      window.history.replaceState(null, '', cleanUrl)
    }

    const stored = localStorage.getItem(TOKEN_KEY)
    if (stored) {
      // Token exists — validate it
      fetchMe(stored).then((u) => {
        if (u) {
          setUser(u)
          setToken(stored)
        } else {
          localStorage.removeItem(TOKEN_KEY)
          setToken(null)
        }
        setIsLoading(false)
      })
    } else {
      // No token — try dev mode (AUTH_MODE=dev returns demo user without token)
      fetch('/api/auth/me').then(async (res) => {
        if (res.ok) {
          const data = await res.json()
          if (data.user) {
            setUser(data.user)
            // Don't set a fake token — dev mode works without one
          }
        }
        setIsLoading(false)
      }).catch(() => {
        setIsLoading(false)
      })
    }
  }, [fetchMe])

  const login = useCallback(async (newToken: string) => {
    setSessionReplaced(false)
    localStorage.setItem(TOKEN_KEY, newToken)
    setToken(newToken)
    const u = await fetchMe(newToken)
    setUser(u)
  }, [fetchMe])

  const logout = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY)
    if (t) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${t}` },
        })
      } catch {
        // ignore
      }
    }
    localStorage.removeItem(TOKEN_KEY)
    setToken(null)
    setUser(null)
  }, [])

  // Handle session expiry fired by apiFetch on 401
  useEffect(() => {
    const handler = () => {
      setToken(null)
      setUser(null)
    }
    window.addEventListener('ws:session-expired', handler)
    return () => window.removeEventListener('ws:session-expired', handler)
  }, [])

  const value = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    token,
    sessionReplaced,
    login,
    logout,
  }), [user, isLoading, token, sessionReplaced, login, logout])

  return React.createElement(AuthContext.Provider, { value }, children)
}
